// src/components/RoleProtectedRoute.js
// Route protection component that requires a minimum role level

import { Navigate } from 'react-router-dom';
import { useRBAC } from '../hooks/useRBAC';
import { useAuth } from '../contexts/AuthContext';

/**
 * A route wrapper that checks if the user has the required role.
 * Redirects to /dashboard if the user doesn't have permission.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authorized
 * @param {string} props.minimumRole - The minimum role required (e.g., 'ADMIN', 'VIEWER')
 * @param {string} props.scope - The scope level to check (default: 'ORG')
 * @param {number} props.scopeEntityId - Optional specific entity ID (defaults to current org)
 * @param {string} props.redirectTo - Where to redirect if unauthorized (default: '/dashboard')
 */
const RoleProtectedRoute = ({
  children,
  minimumRole = 'VIEWER',
  scope = 'ORG',
  scopeEntityId,
  redirectTo = '/dashboard',
  allowDealBUAdmins = false,
  allowDealBUScoped = false,
  allowSupportSuperAdminView = false,
  superAdminOnly = false
}) => {
  const { hasMinimumRoleAtOrAbove, canAdministerAnyDealOrBusinessUnit, canManageIntegrations, isSuperAdmin, isSupportAdmin } = useRBAC();
  const { initialized } = useAuth();

  // Wait for auth context to load userRoles and orgHierarchy from localStorage
  // before checking permissions. Without this, the check would fail on direct
  // navigation because the useEffect hasn't run yet.
  if (!initialized) {
    return null;
  }

  // Super-admin-only routes (e.g. Platform Config) gate purely on the super-admin
  // flag — no org role or org context required.
  if (superAdminOnly) {
    return isSuperAdmin() ? children : <Navigate to={redirectTo} replace />;
  }

  // Get the scope entity ID from props or from localStorage
  let entityId = scopeEntityId;
  if (!entityId) {
    try {
      const selectedOrg = localStorage.getItem('selectedOrganization');
      if (selectedOrg) {
        const org = JSON.parse(selectedOrg);
        entityId = org.organizationId;
      }
    } catch (e) {}
  }

  // If no entity ID available, redirect (can't check permissions)
  if (!entityId) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check if user has the required role
  let hasAccess = hasMinimumRoleAtOrAbove(minimumRole, scope, parseInt(entityId));

  // Fallback: allow Deal/BU admins if configured
  if (!hasAccess && allowDealBUAdmins) {
    hasAccess = canAdministerAnyDealOrBusinessUnit();
  }

  // Fallback: allow Deal/BU-scoped users with the minimum role
  if (!hasAccess && allowDealBUScoped) {
    hasAccess = canManageIntegrations(parseInt(entityId));
  }

  // Fallback: allow super/support admins read-only access to manager-gated modules
  // (they get view-only — write actions inside stay gated on canManage* checks).
  if (!hasAccess && allowSupportSuperAdminView) {
    hasAccess = isSuperAdmin() || isSupportAdmin();
  }

  if (!hasAccess) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default RoleProtectedRoute;
